/**
 * Auth Service
 *
 * Comprehensive authentication service covering:
 * - Basic auth (login, register, logout)
 * - MFA/TOTP
 * - Sessions management
 * - Passkeys/WebAuthn
 * - OAuth providers
 * - Magic links
 * - Device trust
 * - Password security
 * - Email/Phone verification
 * - Audit logs
 */

import { apiClient, setTokens, clearTokens } from '@/lib/api-client';
import type {
  LoginRequest,
  LoginResponse,
  MfaEnrollmentRequiredLoginResponse,
  MfaRequiredLoginResponse,
  SuperAdminLoginRequest,
  SuperAdminLoginResult,
  RegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  User,
  RefreshTokenRequest,
  RefreshTokenResponse,
  // MFA
  MFASetupResponse,
  MFAVerifySetupResponse,
  MFAVerifyRequest,
  MFALoginVerifyRequest,
  MFAEnrollmentSetupRequest,
  MFAEnrollmentVerifyRequest,
  MFAStatus,
  // Sessions
  SessionListResponse,
  // Passkeys
  Passkey,
  PasskeyRegisterOptionsResponse,
  PasskeyAuthenticateOptionsResponse,
  // OAuth
  OAuthProvider,
  OAuthInitRequest,
  OAuthInitResponse,
  OAuthCallbackRequest,
  OAuthProviderInfo,
  // Magic Link
  MagicLinkSendRequest,
  MagicLinkSendResponse,
  MagicLinkVerifyRequest,
  // Device Trust
  TrustedDevice,
  DeviceTrustStatus,
  TrustedDeviceListResponse,
  TrustDeviceResponse,
  // Password Security
  PasswordBreachCheckResponse,
  PasswordSecurityCheckResponse,
  // Captcha
  CaptchaConfig,
  // Email/Phone Verification
  EmailVerificationStatus,
  PhoneVerificationStatus,
  SendPhoneVerificationRequest,
  VerifyPhoneRequest,
  // Audit
  AuthAuditLogFilters,
  AuthAuditLogListResponse,
  AuthAuditStats,
  SecurityAlert,
  // Config
  AuthConfig,
  PasswordExpiryStatus,
  PublicTenantAuthConfig,
  StepUpRequest,
  StepUpResponse,
  StepUpOperation,
  UserEmailIdentities,
  SecondaryEmailRequestResult,
  // Public Verification (for login flow)
  VerificationRequiredResponse,
  PublicVerifyRequest,
  PublicResendVerificationRequest,
  VerificationResult,
  PublicVerificationStatus,
} from '../types/auth.types';

const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const withStepUpToken = (stepUpToken?: string) =>
  stepUpToken ? { headers: { 'X-Step-Up-Token': stepUpToken } } : undefined;

const withStepUpTokenAndParams = (
  stepUpToken: string | undefined,
  params: Record<string, unknown>,
) => ({
  params,
  ...(stepUpToken ? { headers: { 'X-Step-Up-Token': stepUpToken } } : {}),
});

// =============================================================================
// BASIC AUTH
// =============================================================================

export const authApi = {
  // Login - may return tokens or verification required response
  login: async (
    credentials: LoginRequest,
  ): Promise<
    | LoginResponse
    | VerificationRequiredResponse
    | MfaRequiredLoginResponse
    | MfaEnrollmentRequiredLoginResponse
  > => {
    const response = await apiClient.post<
      | LoginResponse
      | VerificationRequiredResponse
      | MfaRequiredLoginResponse
      | MfaEnrollmentRequiredLoginResponse
    >('/auth/login', credentials);
    // Only set tokens if we got a successful login (not verification required)
    if ('accessToken' in response && response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  // SuperAdmin Login
  superadminLogin: async (credentials: SuperAdminLoginRequest): Promise<SuperAdminLoginResult> => {
    const response = await apiClient.post<SuperAdminLoginResult>(
      '/auth/superadmin/login',
      credentials,
    );
    if ('accessToken' in response && response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  // Register (no tokens returned - user must verify email first)
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    return await apiClient.post<RegisterResponse>('/auth/tenant/signup', data);
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      clearTokens();
    }
  },

  // Get profile
  getProfile: async (): Promise<User> => {
    return await apiClient.get<User>('/auth/profile');
  },

  // Update profile
  updateProfile: async (updates: Partial<User>): Promise<User> => {
    return await apiClient.patch<User>('/auth/profile', updates);
  },

  // Refresh token
  refreshToken: async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', data);
    setTokens(response.accessToken, response.refreshToken);
    return response;
  },

  // Get auth config
  getConfig: async (): Promise<AuthConfig> => {
    return await apiClient.get<AuthConfig>('/auth/config');
  },

  getPasswordExpiryStatus: async (): Promise<PasswordExpiryStatus> => {
    return await apiClient.get<PasswordExpiryStatus>('/auth/enterprise/password-expiry/check');
  },
};

export const publicTenantAuthConfigApi = {
  get: async (tenantSlug: string): Promise<PublicTenantAuthConfig> => {
    return await apiClient.get<PublicTenantAuthConfig>('/auth/public-config', {
      params: { tenantSlug },
    });
  },
};

// =============================================================================
// STEP-UP AUTH
// =============================================================================

export const stepUpApi = {
  request: async (data: StepUpRequest): Promise<StepUpResponse> => {
    return await apiClient.post<StepUpResponse>('/auth/enterprise/step-up/verify', data);
  },

  operations: async (): Promise<{ operations: StepUpOperation[] }> => {
    return await apiClient.get<{ operations: StepUpOperation[] }>(
      '/auth/enterprise/step-up/operations',
    );
  },
};

// =============================================================================
// PASSWORD
// =============================================================================

export const passwordApi = {
  // Forgot password
  forgotPassword: async (data: ForgotPasswordRequest): Promise<{ message: string }> => {
    return await apiClient.post('/auth/forgot-password', data);
  },

  // Reset password
  resetPassword: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
    return await apiClient.post('/auth/reset-password', data);
  },

  // Change password
  changePassword: async (data: ChangePasswordRequest): Promise<{ message: string }> => {
    return await apiClient.post('/auth/password/change', data);
  },

  // Check password breach
  checkBreach: async (password: string): Promise<PasswordBreachCheckResponse> => {
    return await apiClient.post('/auth/password/check-breach', { password });
  },

  // Security check
  securityCheck: async (password: string): Promise<PasswordSecurityCheckResponse> => {
    return await apiClient.post('/auth/password/security-check', { password });
  },
};

// =============================================================================
// MFA / TOTP
// =============================================================================

export const mfaApi = {
  // Setup MFA
  setup: async (stepUpToken?: string): Promise<MFASetupResponse> => {
    const config = withStepUpToken(stepUpToken);
    return config
      ? await apiClient.post('/auth/mfa/setup', {}, config)
      : await apiClient.post('/auth/mfa/setup');
  },

  // Verify setup
  verifySetup: async (data: MFAVerifyRequest): Promise<MFAVerifySetupResponse> => {
    return await apiClient.post<MFAVerifySetupResponse>('/auth/mfa/verify', data);
  },

  // Verify MFA code (during login)
  verify: async (data: MFALoginVerifyRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/mfa/challenge', data);
    if (response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  setupEnrollment: async (data: MFAEnrollmentSetupRequest): Promise<MFASetupResponse> => {
    return await apiClient.post<MFASetupResponse>('/auth/mfa/enrollment/setup', data);
  },

  verifyEnrollment: async (data: MFAEnrollmentVerifyRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/mfa/enrollment/verify', data);
    if (response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  // Get MFA status
  getStatus: async (): Promise<MFAStatus> => {
    return await apiClient.get('/auth/mfa/status');
  },

  // Regenerate backup codes
  regenerateBackupCodes: async (stepUpToken?: string): Promise<{ backupCodes: string[] }> => {
    const config = withStepUpToken(stepUpToken);
    return config
      ? await apiClient.post('/auth/mfa/backup-codes', {}, config)
      : await apiClient.post('/auth/mfa/backup-codes');
  },

  // Disable MFA
  disable: async (data: MFAVerifyRequest, stepUpToken?: string): Promise<{ message: string }> => {
    return await apiClient.delete('/auth/mfa', {
      data,
      ...(stepUpToken ? { headers: { 'X-Step-Up-Token': stepUpToken } } : {}),
    });
  },
};

// =============================================================================
// SESSIONS
// =============================================================================

export const sessionsApi = {
  // List sessions
  list: async (): Promise<SessionListResponse> => {
    return await apiClient.get('/auth/sessions');
  },

  // Revoke specific session
  revoke: async (sessionId: string): Promise<{ message: string }> => {
    return await apiClient.delete(`/auth/sessions/${sessionId}`);
  },

  // Revoke all sessions (except current)
  revokeAll: async (stepUpToken?: string): Promise<{ message: string; revokedCount: number }> => {
    const config = withStepUpToken(stepUpToken);
    return config
      ? await apiClient.post('/auth/sessions/revoke-all', {}, config)
      : await apiClient.post('/auth/sessions/revoke-all');
  },

  // Revoke all other sessions and clear local tokens when the UI is ending this browser session.
  revokeAllIncludingCurrent: async (
    stepUpToken?: string,
  ): Promise<{ message: string; revokedCount: number }> => {
    const response = await apiClient.post<{ message: string; revokedCount: number }>(
      '/auth/sessions/revoke-all',
      {},
      withStepUpTokenAndParams(stepUpToken, { includeCurrent: true }),
    );
    clearTokens();
    return response;
  },
};

// =============================================================================
// PASSKEYS / WEBAUTHN
// =============================================================================

export const passkeysApi = {
  // Get registration options
  getRegisterOptions: async (passkeyName?: string): Promise<PasskeyRegisterOptionsResponse> => {
    return await apiClient.post('/auth/passkey/register/options', { passkeyName });
  },

  // Complete registration
  register: async (
    credential: PublicKeyCredential,
    challenge: string,
    passkeyName?: string,
    stepUpToken?: string,
  ): Promise<Passkey> => {
    const payload = {
      response: {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        response: {
          clientDataJSON: arrayBufferToBase64Url(
            (credential.response as AuthenticatorAttestationResponse).clientDataJSON,
          ),
          attestationObject: arrayBufferToBase64Url(
            (credential.response as AuthenticatorAttestationResponse).attestationObject,
          ),
        },
        type: credential.type,
      },
      challenge,
      passkeyName,
    };
    const config = withStepUpToken(stepUpToken);
    const response = config
      ? await apiClient.post<Passkey>('/auth/passkey/register/verify', payload, config)
      : await apiClient.post<Passkey>('/auth/passkey/register/verify', payload);
    return response;
  },

  // Get authentication options
  getAuthenticateOptions: async (input: {
    email: string;
    tenantId?: string;
    tenantSlug?: string;
  }): Promise<PasskeyAuthenticateOptionsResponse> => {
    return await apiClient.post('/auth/passkey/authenticate/options', input);
  },

  // Authenticate with passkey
  authenticate: async (
    credential: PublicKeyCredential,
    challenge: string,
    tenantId: string,
  ): Promise<LoginResponse> => {
    const assertionResponse = credential.response as AuthenticatorAssertionResponse;
    const userHandle = assertionResponse.userHandle;
    const response = await apiClient.post<LoginResponse>('/auth/passkey/authenticate/verify', {
      response: {
        id: credential.id,
        rawId: arrayBufferToBase64Url(credential.rawId),
        response: {
          clientDataJSON: arrayBufferToBase64Url(assertionResponse.clientDataJSON),
          authenticatorData: arrayBufferToBase64Url(assertionResponse.authenticatorData),
          signature: arrayBufferToBase64Url(assertionResponse.signature),
          userHandle: userHandle ? arrayBufferToBase64Url(userHandle) : undefined,
        },
        type: credential.type,
      },
      challenge,
      tenantId,
    });
    if (response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  // List passkeys
  list: async (): Promise<Passkey[]> => {
    return await apiClient.get('/auth/passkey/list');
  },

  // Rename passkey
  rename: async (passkeyId: string, name: string): Promise<Passkey> => {
    return await apiClient.patch(`/auth/passkey/${passkeyId}`, { name });
  },

  // Delete passkey
  delete: async (passkeyId: string, stepUpToken?: string): Promise<{ message: string }> => {
    const config = withStepUpToken(stepUpToken);
    return config
      ? await apiClient.delete(`/auth/passkey/${passkeyId}`, config)
      : await apiClient.delete(`/auth/passkey/${passkeyId}`);
  },
};

// =============================================================================
// OAUTH
// =============================================================================

export const oauthApi = {
  // Get available providers
  getProviders: async (): Promise<OAuthProviderInfo[]> => {
    return await apiClient.get('/auth/oauth/providers');
  },

  // Initialize OAuth flow
  init: async (data: OAuthInitRequest): Promise<OAuthInitResponse> => {
    return await apiClient.post('/auth/oauth/init', data);
  },

  // Handle OAuth callback
  callback: async (data: OAuthCallbackRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/oauth/callback', data);
    if (response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  // Unlink OAuth provider
  unlink: async (provider: OAuthProvider): Promise<{ message: string }> => {
    return await apiClient.delete('/auth/oauth/unlink', { data: { provider } });
  },
};

// =============================================================================
// MAGIC LINK
// =============================================================================

export const magicLinkApi = {
  // Send magic link
  send: async (data: MagicLinkSendRequest): Promise<MagicLinkSendResponse> => {
    return await apiClient.post<MagicLinkSendResponse>('/auth/magic-link/request', data);
  },

  // Verify magic link
  verify: async (data: MagicLinkVerifyRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/auth/magic-link/verify', data);
    if (response.accessToken) {
      setTokens(response.accessToken, response.refreshToken);
    }
    return response;
  },

  // Revoke magic link
  revoke: async (token: string): Promise<{ message: string }> => {
    return await apiClient.delete('/auth/magic-link/revoke', { data: { token } });
  },
};

// =============================================================================
// DEVICE TRUST
// =============================================================================

export const deviceTrustApi = {
  // Get trust status for current device
  getStatus: async (): Promise<DeviceTrustStatus> => {
    return await apiClient.get('/auth/devices/trust-status');
  },

  // List trusted devices
  list: async (): Promise<TrustedDeviceListResponse> => {
    return await apiClient.get('/auth/devices/trusted');
  },

  // Trust current device
  trustDevice: async (name?: string, stepUpToken?: string): Promise<TrustedDevice> => {
    const payload = {
      deviceName: name,
    };
    const config = withStepUpToken(stepUpToken);
    const response = config
      ? await apiClient.post<TrustDeviceResponse>('/auth/devices/trust', payload, config)
      : await apiClient.post<TrustDeviceResponse>('/auth/devices/trust', payload);
    return response.device;
  },

  // Rename trusted device
  rename: async (deviceId: string, name: string): Promise<TrustedDevice> => {
    return await apiClient.patch(`/auth/devices/trusted/${deviceId}`, { deviceName: name });
  },

  // Revoke trusted device
  revoke: async (deviceId: string): Promise<{ message: string }> => {
    return await apiClient.delete(`/auth/devices/trusted/${deviceId}`);
  },

  // Revoke all trusted devices
  revokeAll: async (
    includeCurrent = false,
    stepUpToken?: string,
  ): Promise<{ message: string; revokedCount: number }> => {
    return await apiClient.delete('/auth/devices/trusted', {
      params: includeCurrent ? { includeCurrent: true } : undefined,
      ...(stepUpToken ? { headers: { 'X-Step-Up-Token': stepUpToken } } : {}),
    });
  },
};

// =============================================================================
// EMAIL VERIFICATION
// =============================================================================

export const emailVerificationApi = {
  // Send verification email
  send: async (email?: string): Promise<{ message: string }> => {
    return await apiClient.post('/auth/email/send-verification', email ? { email } : undefined);
  },

  // Verify email
  verify: async (token: string): Promise<{ message: string }> => {
    return await apiClient.post('/auth/email/verify', { token });
  },

  // Get status
  getStatus: async (): Promise<EmailVerificationStatus> => {
    return await apiClient.get('/auth/email/status');
  },
};

export const emailIdentitiesApi = {
  get: async (): Promise<UserEmailIdentities> => {
    return await apiClient.get<UserEmailIdentities>('/auth/email-identities');
  },

  setSecondary: async (
    email: string,
    stepUpToken: string,
  ): Promise<SecondaryEmailRequestResult> => {
    return await apiClient.put<SecondaryEmailRequestResult>(
      '/auth/email-identities/secondary',
      { email },
      withStepUpToken(stepUpToken),
    );
  },

  verifySecondary: async (code: string): Promise<{ message: string }> => {
    return await apiClient.post('/auth/email-identities/secondary/verify', { code });
  },

  removeSecondary: async (stepUpToken: string): Promise<{ message: string }> => {
    return await apiClient.delete('/auth/email-identities/secondary', withStepUpToken(stepUpToken));
  },
};

// =============================================================================
// PHONE VERIFICATION
// =============================================================================

export const phoneVerificationApi = {
  // Send verification code
  send: async (data: SendPhoneVerificationRequest): Promise<{ message: string }> => {
    return await apiClient.post('/auth/phone/send-verification', data);
  },

  // Verify phone
  verify: async (data: VerifyPhoneRequest): Promise<{ message: string }> => {
    return await apiClient.post('/auth/phone/verify', data);
  },

  // Get status
  getStatus: async (): Promise<PhoneVerificationStatus> => {
    return await apiClient.get('/auth/phone/status');
  },
};

// =============================================================================
// CAPTCHA
// =============================================================================

export const captchaApi = {
  // Get captcha config
  getConfig: async (): Promise<CaptchaConfig> => {
    return await apiClient.get('/auth/captcha/config');
  },
};

// =============================================================================
// PUBLIC VERIFICATION (for login flow - no auth required)
// =============================================================================

export const publicVerificationApi = {
  // Verify email with OTP (public - no auth)
  verifyEmail: async (data: PublicVerifyRequest): Promise<VerificationResult> => {
    return await apiClient.post<VerificationResult>('/auth/verify/email', data);
  },

  // Verify company email with OTP (public - no auth)
  verifyCompanyEmail: async (data: PublicVerifyRequest): Promise<VerificationResult> => {
    return await apiClient.post<VerificationResult>('/auth/verify/company-email', data);
  },

  // Verify phone with OTP (public - no auth)
  verifyPhone: async (data: PublicVerifyRequest): Promise<VerificationResult> => {
    return await apiClient.post<VerificationResult>('/auth/verify/phone', data);
  },

  // Resend email verification OTP (public - no auth)
  resendEmailOtp: async (data: PublicResendVerificationRequest): Promise<VerificationResult> => {
    return await apiClient.post<VerificationResult>('/auth/verify/email/resend', data);
  },

  // Resend company email verification OTP (public - no auth)
  resendCompanyEmailOtp: async (
    data: PublicResendVerificationRequest,
  ): Promise<VerificationResult> => {
    return await apiClient.post<VerificationResult>('/auth/verify/company-email/resend', data);
  },

  // Resend phone verification OTP (public - no auth)
  resendPhoneOtp: async (data: PublicResendVerificationRequest): Promise<VerificationResult> => {
    return await apiClient.post<VerificationResult>('/auth/verify/phone/resend', data);
  },

  // Get verification status (public - no auth)
  getStatus: async (email: string, tenantSlug: string): Promise<PublicVerificationStatus> => {
    return await apiClient.get<PublicVerificationStatus>(
      `/auth/verify/status?email=${encodeURIComponent(email)}&tenantSlug=${encodeURIComponent(tenantSlug)}`,
    );
  },
};

// =============================================================================
// AUDIT LOGS
// =============================================================================

export const authAuditApi = {
  // Get audit logs
  list: async (filters?: AuthAuditLogFilters): Promise<AuthAuditLogListResponse> => {
    const params = new URLSearchParams();
    if (filters?.action) params.append('action', filters.action);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    return await apiClient.get(`/auth/audit/logs?${params}`);
  },

  // Get audit stats
  getStats: async (): Promise<AuthAuditStats> => {
    return await apiClient.get('/auth/audit/stats');
  },

  // Get security alerts
  getSecurityAlerts: async (): Promise<SecurityAlert[]> => {
    return await apiClient.get('/auth/audit/security-alerts');
  },

  // Get my activity
  getMyActivity: async (filters?: AuthAuditLogFilters): Promise<AuthAuditLogListResponse> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    return await apiClient.get(`/auth/audit/my-activity?${params}`);
  },
};

// =============================================================================
// LEGACY AUTH SERVICE CLASS (for backward compatibility)
// =============================================================================

class AuthService {
  async login(
    credentials: LoginRequest,
  ): Promise<
    | LoginResponse
    | VerificationRequiredResponse
    | MfaRequiredLoginResponse
    | MfaEnrollmentRequiredLoginResponse
  > {
    return authApi.login(credentials);
  }

  async superadminLogin(credentials: SuperAdminLoginRequest): Promise<SuperAdminLoginResult> {
    return authApi.superadminLogin(credentials);
  }

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    return authApi.register(data);
  }

  async logout(): Promise<void> {
    return authApi.logout();
  }

  async getProfile(): Promise<User> {
    return authApi.getProfile();
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    return authApi.updateProfile(updates);
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<{ message: string }> {
    return passwordApi.forgotPassword(data);
  }

  async resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    return passwordApi.resetPassword(data);
  }

  async changePassword(data: ChangePasswordRequest): Promise<{ message: string }> {
    return passwordApi.changePassword(data);
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return emailVerificationApi.verify(token);
  }

  async resendVerification(email?: string): Promise<{ message: string }> {
    return emailVerificationApi.send(email);
  }
}

export const authService = new AuthService();

// =============================================================================
// CONSOLIDATED EXPORT
// =============================================================================

export const authServices = {
  auth: authApi,
  publicTenantAuthConfig: publicTenantAuthConfigApi,
  password: passwordApi,
  mfa: mfaApi,
  sessions: sessionsApi,
  passkeys: passkeysApi,
  oauth: oauthApi,
  magicLink: magicLinkApi,
  deviceTrust: deviceTrustApi,
  emailIdentities: emailIdentitiesApi,
  emailVerification: emailVerificationApi,
  phoneVerification: phoneVerificationApi,
  captcha: captchaApi,
  audit: authAuditApi,
};
